/**
 * TotalProductionCounter Web Component
 *
 * A standalone web component that displays real-time solar production statistics
 * with an animated counter effect. Works in both standalone HTML and React applications.
 *
 * Usage in HTML:
 * <total-production-counter api-endpoint="https://your-api.com/graphql"></total-production-counter>
 *
 * Usage in React:
 * import './path/to/TotalProductionCounter'
 * <total-production-counter api-endpoint={endpoint} />
 */

import { AllPlatformStatisticsTotalProductionDocument } from "@/api/graphql"

interface ProductionData {
	data: {
		overall_statistics: {
			total_production_for_counter: string
			production_speed: string
		}
	}
}

class TotalProductionCounter extends HTMLElement {
	private shadow: ShadowRoot
	private value = 0
	private currentData: ProductionData | null = null
	private animationFrameId: number | null = null
	private lastUpdateTime = 0
	private isScrolling = false
	private digitUpdateTimes = new Map<number, number[]>()
	private digitChangeTracking = new Map<
		number,
		{ lastValue: number; changeCount: number; lastChangeTime: number }
	>()
	private fastScrollingDigits = new Set<number>()
	private readonly FAST_SCROLL_THRESHOLD = 200 // If updates come faster than this, use infinite rotation
	private readonly MIN_CHANGE_FREQUENCY = 3 // Minimum changes per second to consider "too fast"

	constructor() {
		super()
		this.shadow = this.attachShadow({ mode: "open" })
		this.render()
	}

	static get observedAttributes() {
		return ["api-endpoint", "update-interval"]
	}

	connectedCallback() {
		setTimeout(() => {
			void this.startDataFetching()
		}, 100)
	}

	disconnectedCallback() {
		this.stopContinuousScrolling()
		this.digitUpdateTimes.clear()
		this.digitChangeTracking.clear()
		this.fastScrollingDigits.clear()
	}

	attributeChangedCallback(
		_name: string,
		oldValue: string,
		newValue: string,
	) {
		if (oldValue !== newValue) {
			void this.startDataFetching()
		}
	}

	private async fetchProductionData(): Promise<ProductionData | null> {
		const endpoint = this.getAttribute("api-endpoint")

		if (!endpoint) {
			console.warn(
				"TotalProductionCounter: api-endpoint attribute is required",
			)
			return null
		}

		const endDate = new Date().toISOString()
		const query = AllPlatformStatisticsTotalProductionDocument

		try {
			const response = await fetch(endpoint, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					query,
					variables: {
						start: "1970-01-01T00:00:00Z",
						end: endDate,
					},
				}),
			})

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`)
			}

			const data = (await response.json()) as ProductionData
			return data
		} catch (error) {
			console.error("Failed to fetch production data:", error)
			return null
		}
	}

	private async startDataFetching() {
		this.renderLoadingState()

		this.currentData = await this.fetchProductionData()

		if (this.currentData?.data?.overall_statistics) {
			const initialValue =
				parseFloat(
					this.currentData.data.overall_statistics
						.total_production_for_counter || "0",
				) / 1000000 // Convert Wh to MWh

			this.value = initialValue
			this.renderCounter()

			setTimeout(() => {
				this.animateToValue(Math.round(this.value))
				this.startContinuousScrolling()
			}, 100)
		}
	}

	private animateToValue(targetValue: number) {
		const counterElement = this.shadow.querySelector(
			".counter-value",
		) as HTMLElement
		if (!counterElement) return

		const startValue = 0
		const duration = 2000 // 2 seconds
		const startTime = performance.now()

		const animate = (currentTime: number) => {
			const elapsed = currentTime - startTime
			const progress = Math.min(elapsed / duration, 1)

			// Easing function for smooth animation
			const easedProgress = 1 - Math.pow(1 - progress, 3)
			const currentValue = Math.round(
				startValue + (targetValue - startValue) * easedProgress,
			)

			this.updateCounterDisplay(currentValue)

			if (progress < 1) {
				this.animationFrameId = requestAnimationFrame(animate)
			}
		}

		this.animationFrameId = requestAnimationFrame(animate)
	}

	private updateCounterDisplay(value: number) {
		const counterElement = this.shadow.querySelector(
			".counter-value",
		) as HTMLElement
		if (!counterElement) return

		const valueString = Math.floor(value).toLocaleString()
		const digits = valueString.split("")

		// Get current structure
		const currentDigitContainers = Array.from(
			counterElement.querySelectorAll(".digit-container"),
		)
		let digitIndex = 0

		// Check if we need to rebuild the structure
		const currentDigitCount = currentDigitContainers.length
		const newDigitCount = digits.filter((d) => d.match(/\d/)).length

		if (currentDigitCount !== newDigitCount) {
			// Rebuild the entire structure - reset tracking for all digits
			this.digitUpdateTimes.clear()
			this.digitChangeTracking.clear()
			this.fastScrollingDigits.clear()
			counterElement.innerHTML = ""
			digits.forEach((digit, index) => {
				if (digit.match(/\d/)) {
					// Create odometer-style digit container
					const digitContainer = document.createElement("span")
					digitContainer.className = "digit-container"
					digitContainer.style.animationDelay = `${index * 50}ms`

					const digitRoll = document.createElement("div")
					digitRoll.className = "digit-roll"

					// Create all digits 0-9 for smooth scrolling
					for (let i = 0; i <= 9; i++) {
						const digitSpan = document.createElement("span")
						digitSpan.className = "digit"
						digitSpan.textContent = i.toString()
						digitRoll.appendChild(digitSpan)
					}

					digitContainer.appendChild(digitRoll)
					counterElement.appendChild(digitContainer)

					// Set initial position
					const digitValue = parseInt(digit, 10)
					this.setDigitPosition(
						digitRoll,
						isNaN(digitValue) ? 0 : digitValue,
						digitIndex,
					)
					digitIndex++
				} else {
					// Separator (comma, space, etc.)
					const separatorSpan = document.createElement("span")
					separatorSpan.className = "separator"
					separatorSpan.textContent = digit
					counterElement.appendChild(separatorSpan)
				}
			})
		} else {
			// Update existing digits with smooth scrolling
			digits.forEach((digit) => {
				if (digit.match(/\d/)) {
					const digitContainer = currentDigitContainers[digitIndex]
					if (digitContainer) {
						const digitRoll = digitContainer.querySelector(
							".digit-roll",
						) as HTMLElement
						const targetDigit = parseInt(digit, 10)
						this.setDigitPosition(
							digitRoll,
							isNaN(targetDigit) ? 0 : targetDigit,
							digitIndex,
						)
						digitIndex++
					}
				}
			})
		}
	}

	private setDigitPosition(
		digitRoll: HTMLElement,
		digit: number,
		digitIndex: number,
	) {
		const currentTime = performance.now()
		const normalizedDigit = digit % 10

		// Track digit value changes to detect rapid changes
		const tracking = this.digitChangeTracking.get(digitIndex) ?? {
			lastValue: -1,
			changeCount: 0,
			lastChangeTime: 0,
		}

		// Check if this digit actually changed value
		const digitChanged = tracking.lastValue !== normalizedDigit

		if (digitChanged) {
			tracking.changeCount++
			tracking.lastChangeTime = currentTime
			tracking.lastValue = normalizedDigit
			this.digitChangeTracking.set(digitIndex, tracking)
		}

		// Track update frequency for this digit position
		if (!this.digitUpdateTimes.has(digitIndex)) {
			this.digitUpdateTimes.set(digitIndex, [])
		}

		const updateTimes = this.digitUpdateTimes.get(digitIndex)!
		if (digitChanged) {
			updateTimes.push(currentTime)
		}

		// Keep only recent updates (last 1 second)
		const recentUpdates = updateTimes.filter(
			(time) => currentTime - time <= 1000,
		)
		this.digitUpdateTimes.set(digitIndex, recentUpdates)

		// Reset change count if it's been more than 1 second since last change
		if (currentTime - tracking.lastChangeTime > 1000) {
			tracking.changeCount = 0
			this.digitChangeTracking.set(digitIndex, tracking)
		}

		// Check if this digit should use infinite rotation
		const isScrollingFast = this.shouldUseInfiniteRotation(
			recentUpdates,
			tracking.changeCount,
		)

		if (isScrollingFast && !this.fastScrollingDigits.has(digitIndex)) {
			this.fastScrollingDigits.add(digitIndex)
			this.startInfiniteRotation(digitRoll)
		} else if (
			!isScrollingFast &&
			this.fastScrollingDigits.has(digitIndex)
		) {
			this.fastScrollingDigits.delete(digitIndex)
			this.stopInfiniteRotation(digitRoll)
		}

		// Only update position if not in infinite rotation mode
		if (!this.fastScrollingDigits.has(digitIndex)) {
			const translateY = -normalizedDigit * 1.2
			digitRoll.style.transform = `translateY(${translateY}em)`
		}
	}

	private shouldUseInfiniteRotation(
		updateTimes: number[],
		changeCount: number,
	): boolean {
		if (updateTimes.length < 3) return false

		// Calculate average time between actual value changes
		const intervals: number[] = []
		for (let i = 1; i < updateTimes.length; i++) {
			intervals.push(updateTimes[i] - updateTimes[i - 1])
		}

		if (intervals.length === 0) return false

		const averageInterval =
			intervals.reduce((a, b) => a + b, 0) / intervals.length

		// A digit is "too fast" if:
		// 1. It's changing values frequently (more than MIN_CHANGE_FREQUENCY per second)
		// 2. AND the average time between changes is faster than our animation can handle
		const changesPerSecond = changeCount // changeCount is reset every second
		const isChangingVeryFrequently =
			changesPerSecond >= this.MIN_CHANGE_FREQUENCY
		const isUpdatingTooFast = averageInterval < this.FAST_SCROLL_THRESHOLD

		return isChangingVeryFrequently && isUpdatingTooFast
	}

	private startInfiniteRotation(digitRoll: HTMLElement) {
		digitRoll.style.transition = "none"
		digitRoll.style.animation = "infiniteRotate 0.8s linear infinite"
		// Add subtle blur to indicate rapid movement
		digitRoll.style.filter = "blur(0.5px)"
	}

	private stopInfiniteRotation(digitRoll: HTMLElement) {
		digitRoll.style.animation = "none"
		digitRoll.style.transition =
			"transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
		digitRoll.style.filter = "none"
	}

	private startContinuousScrolling() {
		if (!this.currentData?.data?.overall_statistics?.production_speed) {
			return
		}

		this.isScrolling = true
		this.lastUpdateTime = performance.now()

		const scroll = (currentTime: number) => {
			if (!this.isScrolling) return

			const deltaTime = currentTime - this.lastUpdateTime
			this.lastUpdateTime = currentTime

			if (this.currentData?.data?.overall_statistics?.production_speed) {
				// Convert production speed from watts per 250ms to MWh per millisecond
				const speedWattsPerMs =
					(parseFloat(
						this.currentData.data.overall_statistics
							.production_speed,
					) *
						4) /
					1000
				const speedMWhPerMs = speedWattsPerMs / 1000000

				// Update value based on elapsed time
				this.value += speedMWhPerMs * deltaTime
				this.updateCounterDisplay(this.value)
			}

			this.animationFrameId = requestAnimationFrame(scroll)
		}

		this.animationFrameId = requestAnimationFrame(scroll)
	}

	private stopContinuousScrolling() {
		this.isScrolling = false
		if (this.animationFrameId) {
			cancelAnimationFrame(this.animationFrameId)
			this.animationFrameId = null
		}
	}

	private renderCounter() {
		this.shadow.innerHTML = `
      ${this.getStyles()}
      <div class="container">
        <div class="counter-wrapper">
          <div class="counter-value"></div>
          <div class="unit-label">MWh</div>
        </div>
      </div>
    `
	}

	private renderLoadingState() {
		this.shadow.innerHTML = `
      ${this.getStyles()}
      <div class="container">
        <div class="counter-wrapper">
          <div class="counter-value loading">---</div>
          <div class="unit-label">MWh</div>
        </div>
      </div>
    `
	}

	private render() {
		this.renderLoadingState()
	}

	private getStyles(): string {
		return `
      <style>
        .container {
          display: flex;
          flex-direction: column;
          align-items: center;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .counter-wrapper {
          display: flex;
          align-items: center;
          background: #f3f4f6;
          border-radius: 2rem;
          padding: 1rem;
          color: #4e5155;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        }

        .counter-wrapper.error {
          background: #fef2f2;
          color: #dc2626;
        }

        .counter-value {
          font-family: 'Courier New', monospace;
          font-size: 22px;
          font-weight: 700;
          line-height: 1em;
          display: flex;
          align-items: center;
          height: 1.2em;
        }

        .digit-container {
          display: inline-block;
          width: 0.8em;
          height: 1.2em;
          overflow: hidden;
          position: relative;
          text-align: center;
          vertical-align: top;
        }

        .digit-roll {
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          width: 100%;
          height: auto;
        }

        .digit {
          display: block;
          height: 1.2em;
          line-height: 1.2em;
          text-align: center;
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .separator {
          display: inline-block;
          margin: 0 1px;
          height: 1.2em;
          line-height: 1.2em;
        }

        .counter-value.loading {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        .unit-label {
          font-size: 22px;
          font-weight: bold;
          margin-left: 8px;
        }

        .error-message {
          font-size: 22px;
          font-weight: bold;
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        @keyframes slideUp {
          0% {
            transform: translateY(20px);
            opacity: 0;
          }
          100% {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes infiniteRotate {
          0% {
            transform: translateY(0);
          }
          100% {
            transform: translateY(-12em); /* -10 * 1.2em for all digits */
          }
        }

        .digit-container {
          animation: slideUp 0.5s cubic-bezier(0.4, 0, 0.2, 1) backwards;
        }
      </style>
    `
	}
}

// Define the custom element
customElements.define("total-production-counter", TotalProductionCounter)

// Export for React compatibility
export default TotalProductionCounter
